import { parsePhoneNumberFromString } from "libphonenumber-js";
import { DateTime } from "luxon";
import { z } from "zod";

import { normalizeSimpleDate } from "@/src/lib/utils/date-and-phone-utils";

export const ssnSchema = z
  .string()
  .transform((value) => {
    const digits = value.replace(/\D/g, "").slice(0, 9); // Limit to 9 digits

    if (digits.length < 9) return value; // Return partial input as-is

    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  })
  .refine((value) => {
    const digits = value.replace(/\D/g, "");

    // Allow partial SSNs during input
    if (digits.length < 9) return true;

    // Validate complete SSNs
    const [area, group, serial] = value.split("-");

    return area !== "000" && area !== "666" && parseInt(area) < 900 && group !== "00" && serial !== "0000";
  }, "Invalid Social Security Number format or reserved numbers")
  .describe("Social Security Number that accepts partial input and validates complete SSNs");

export const phoneNumberSchema = z
  .string()
  .transform((value): string | null => {
    const digits = value.replace(/\D/g, "");
    const stripped = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

    if (stripped.length === 10) {
      return `${stripped.slice(0, 3)}-${stripped.slice(3, 6)}-${stripped.slice(6)}`;
    }

    return null;
  })
  .refine((value) => {
    if (value === null) return true;

    try {
      const phoneNumberObj = parsePhoneNumberFromString(value, "US");

      return phoneNumberObj && phoneNumberObj.isValid();
    } catch {
      return false;
    }
  }, "Warning: Phone number is not a valid US phone number")
  .describe("Phone number containing only digits and hyphens, in the format 123-456-7890");

export function normalizePhoneNumber(value: string | null | undefined): string | null {
  if (value == null || value.trim() === "") return null;
  const result = phoneNumberSchema.safeParse(value.trim());

  return result.success && result.data != null ? result.data : null;
}

export const emailSchema = z
  .string()
  .min(5, "Email address is too short")
  .max(254, "Email address is too long")
  .email({ message: "Please enter a valid email address" })
  .or(z.literal(""))
  .describe("Email address in the format user@example.com otherwise assign null");

export const stytchOrganizationIdSchema = z.string().startsWith("organization-");

export const simpleDateSchema = z.string().transform(normalizeSimpleDate).describe("The date in format YYYY-MM-DD");

export const npiNumberSchema = z
  .string()
  .transform((value) => value.replace(/\D/g, ""))
  .refine((value) => /^\d{10}$/.test(value), "NPI must be exactly 10 digits")
  .describe("The NPI number is exactly 10 digits");

export const isoDateTimeSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(?:Z|[+-]\d{2}:\d{2})$/,
    "Datetime must be in ISO 8601 format, e.g. 2023-03-20T15:00:00.000-05:00"
  )
  .describe("The datetime in ISO 8601 format with optional milliseconds and timezone offset");

/** A wall-clock calendar day at the clinic, e.g. "August 1st there" — never an instant. */
export type GroupLocalDate = string & { __brand: "GroupLocalDate" };

/** An ISO 8601 instant normalized to UTC, which is all the DB and our RPCs accept. */
export type UtcInstant = string & { __brand: "UtcInstant" };

/**
 * The only producer of a {@link GroupLocalDate}. Cross into instants via `localDateToUtcInstant`;
 * the brands make skipping that conversion a type error rather than a silent offset.
 */
export const groupLocalDateSchema = z
  .string()
  // Tolerates a full ISO datetime, since bookmarked analytics URLs predate this validation.
  .transform((value) => normalizeSimpleDate(value.slice(0, 10)))
  .refine((value): value is string => value !== null, "Expected a calendar date in YYYY-MM-DD format")
  // Well-formed is not the same as real: "2026-02-31" matches the shape but has no instant, and
  // would otherwise fail as an unhandled ZodError inside the RPC layer rather than a 400 here.
  .refine((value) => DateTime.fromISO(value).isValid, "Expected a date that exists on the calendar")
  .transform((value) => value as GroupLocalDate)
  .describe("A calendar date (YYYY-MM-DD) interpreted in the group's timezone");

/** The only producer of a {@link UtcInstant}. Normalizes any accepted offset to UTC. */
export const utcInstantSchema = isoDateTimeSchema
  .transform((value) => DateTime.fromISO(value).toUTC().toISO()! as UtcInstant)
  .describe("An ISO 8601 instant normalized to UTC");

export const SIGNATURE_PAGE_NUMBER_PROMPT =
  'If the document needs a signature, identify every 1-based PDF page number that contains the actual unsigned signature line, signature box, or blank provider/physician signature field. Return comma-separated page numbers in ascending order with no spaces (e.g. "3,6,9"). Use null only when no signature location is present or the document does not need a signature.';

/** Page reference for signature placement; coerces blank to null. */
export const nullableSignaturePageNumberSchema = z
  .string()
  .transform((value): string | null => (value.trim() === "" ? null : value))
  .nullable()
  .describe(SIGNATURE_PAGE_NUMBER_PROMPT);

// Keyed on standing alone rather than on being handwritten: on a handwritten form every word is
// handwritten, so "added by hand" is no discriminator there. Biased toward false on purpose — a
// false ADW diverts the document out of its normal routing, while a missed one costs nothing beyond
// the review someone would have done anyway.
export const IS_ADW_PROMPT =
  'True when the letters "ADW" (Already Dealt With) have been added onto the document by clinic staff as a standalone mark: hand-printed or stamped by itself in blank whitespace, a page corner, or a margin — usually near the top of the first page, and often in an ink colour that contrasts with the rest of the document. The three letters must be separately legible as A, D and W; case and punctuation vary ("ADW", "adw", "A.D.W.").\n\nBeing handwritten is not by itself evidence. On a handwritten form or note every word is handwritten, so decide instead whether the letters stand alone as a mark *about* the document rather than as content *of* the document. Set false when the letters are part of a word, phrase, or sentence; when they sit on a ruled line or inside a filled-in form field (for example Subjective, Objective, Assessment, Plan, diagnosis, medication, or signature areas); or when they appear in printed body text, a company or product name, a form field label, or a printed abbreviation.\n\nCursive or joined-up handwriting that merely resembles "adw" is not an ADW marking. If you cannot read the three letters distinctly, or the mark could just as well be an ordinary clinical word, set false. Set false when no such marking is present.';

/**
 * Document-level "Already Dealt With" marking. Clinic staff hand-write "ADW" on a document they have
 * already actioned; downstream it is filed to the chart and closed instead of being left open for review.
 * Classification-agnostic — an ADW document can be of any type.
 */
export const isAdwSchema = z.boolean().nullable().describe(IS_ADW_PROMPT);

export const FAX_ADDRESSEE_PROMPT =
  "The single person this fax/document names as addressee, as one \"Lastname, Firstname\" value, regardless of whether they are a licensed provider — e.g. a nurse or other staff member with no NPI. Look across the entire document, not just a page-1 cover sheet: an explicit 'To:'/'Attn:' line, or a signature/closing line on any page (e.g. 'Regards, Tracy Nilsson') can name the addressee — a response fax often includes a copy of the original outgoing letter signed by the person who sent it. Distinct from the receivingProvider block, which only surfaces a name that matches a known practitioner. Groups that restrict this field to an `options` list in blocks.yaml expect exactly one of those values.";

/**
 * Who a fax is addressed to, independent of the practitioner/NPI roster. Shared across the carrier
 * blocks that groups route on, so the roster-dropdown wiring is identical wherever it is enabled.
 */
export const faxAddresseeSchema = z.string().nullable().describe(FAX_ADDRESSEE_PROMPT);

/** Comma-separated query param → validated enum array (e.g. "imaging,procedure"). */
export const commaSeparatedEnumList = <T extends Record<string, string>>(enumObject: T) =>
  z
    .string()
    .transform((value) =>
      value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
    )
    .pipe(z.array(z.nativeEnum(enumObject)).min(1))
    .optional();
